/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Injector, OnDestroy} from '@angular/core';
import * as _ from 'lodash';

import {OBJECT_TYPE, ObjectTypeFinder} from '../../common/constants/object-type';
import {Uuid} from '../../../components/util/uuid';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './version-copy-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {createBufferedFlag, readBufferedFlag} from '../../common/util/template-state';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-version-copy-page',
  template
})
export class VersionCopyPageComponent implements OnDestroy {
  lastViewed: any[] = [];
  lastViewedObject: any;
  selectFrom = 'LAST_VIEWED';

  loaded = true;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  jsonGetTest = true;
  objectId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  readonly objectTypeKeys = Object.keys(OBJECT_TYPE);

  err = '';
  objData: any;
  jsonInput = '';
  jsonErr = '';
  jsonInferredObj: any;
  jsonObjectId = '';
  jsonObjectNameNew = '';
  setJsonObj: any;
  jsonObjectIdNew = '';
  jsonGetTestObjFound: string;
  jsonSuccess = '';

  private checkedJsonInput: any;
  private savedObjId = '';
  private savedObjUrlParam = '';

  constructor(
    private readonly $state: StateService,
    private readonly injector: Injector,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly cdr: ChangeDetectorRef,
    navigationHistory: NavigationHistoryService
  ) {
    this.lastViewed = buildLastViewedEntries(localUserProfile, LocalNetworkProfile, false);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  checkObject(isInternalCheck?: boolean): void {
    this.jsonErr = '';
    if (!this.jsonInput) {
      return;
    }

    try {
      this.checkedJsonInput = JSON.parse(this.jsonInput);
    } catch (_err) {
      this.jsonErr = 'Inputted JSON is not valid JSON!';
      return;
    }

    if (!isInternalCheck) {
      this.jsonInferredObj = null;
      if (this.checkedJsonInput.url) {
        this.jsonInferredObj = ObjectTypeFinder.findObjectFromUrl(this.checkedJsonInput.url);
      }
      this.jsonObjectId = this.checkedJsonInput.id || '';
      this.jsonObjectNameNew = this.checkedJsonInput.name || '';
    }
  }

  checkSave(andSave?: boolean): void {
    const objType = this.jsonInferredObj || this.setJsonObj;
    this.checkObject(true);

    let Resource: any;
    try {
      Resource = this.getResource(objType.type);
    } catch (_err) {
      this.jsonErr = 'Can\'t find the appropriate end point for a ' + objType;
      return;
    }
    if (!_.isFunction(Resource.save)) {
      this.jsonErr = 'Can\'t find the end point for a ' + objType;
      return;
    }

    this.jsonGetTest = false;
    this.jsonGetTestObjFound = '';
    const objId = this.jsonObjectIdNew || Uuid.generate();

    const doSave = () => {
      this.checkedJsonInput.id = objId;
      this.checkedJsonInput.name = this.jsonObjectNameNew || Uuid.generate();
      Resource.save(this.checkedJsonInput, () => {
        this.jsonSuccess = 'Save took place';
        this.savedObjId = objId;
        this.savedObjUrlParam = objType.stateName;
      }, (err: any) => {
        this.jsonErr = err.data;
      });
    };

    if (andSave) {
      doSave();
      return;
    }

    Resource.get({id: objId}, () => {
      this.jsonGetTest = true;
      this.jsonGetTestObjFound = 'FOUND';
    }, (err: any) => {
      this.jsonGetTest = true;
      this.loaded = true;
      const status = _.get(err, ['status']);
      if (status === 404) {
        this.jsonGetTestObjFound = 'UNFOUNDED';
        doSave();
      } else {
        this.jsonErr = 'An error happened trying to find a duplicate of your object';
      }
    });
  }

  goToObject(): void {
    this.$state.go(this.savedObjUrlParam, {id: this.savedObjId});
  }

  getJson(): void {
    this.err = '';

    const objType = this.selectFrom === 'LAST_VIEWED' ? _.get(this, ['lastViewedObject', 'objectType']) : this.objectType.type;
    const objId = this.selectFrom === 'LAST_VIEWED' ? _.get(this, ['lastViewedObject', 'id']) : this.objectId;

    let Resource: any;
    try {
      Resource = this.getResource(objType);
    } catch (_err) {
      this.err = 'Can\'t find the appropriate end point for a ' + objType;
      return;
    }
    if (!_.isFunction(Resource.get)) {
      this.err = 'Can\'t find the end point for a ' + objType;
      return;
    }
    this.loaded = false;
    Resource.get({id: objId}, (data: any) => {
      this.loaded = true;
      this.objData = _.cloneDeep(data);

      _.forEach(['updateUser', 'createTime', 'createUser', 'updateUser', 'updateTime', 'network', 'velocity'], (field) => {
        delete this.objData[field];
      });
    }, (err: any) => {
      this.loaded = true;
      const status = _.get(err, ['status']);
      if (status === 404) {
        this.err = 'Couldn\'t find ' + objType + ' with an ID of ' + objId;
      } else {
        this.err = 'An error has happened finding the JSON';
      }
    });
  }

  private getResource(token: string): any {
    return this.injector.get(token);
  }
}
